import { intersect } from './finite-set';

export interface IETerm {
  sets: number[];
  sign: 1 | -1;
  size: number;
}

export interface IEResult {
  total: number;
  terms: IETerm[];
}

/**
 * Apply the inclusion-exclusion principle to a list of sets.
 * Iterates every non-empty subset of the set list (by index).
 * sign = (-1)^(k+1) where k = subset size (size-1 → +1, size-2 → −1, …)
 * size = |intersection of sets at those indices|
 * total = sum of sign * size across all subsets
 */
export function inclusionExclusion(sets: string[][]): IEResult {
  const n = sets.length;
  const terms: IETerm[] = [];
  let total = 0;

  // Iterate all non-empty subsets via bitmask
  for (let mask = 1; mask < 1 << n; mask++) {
    const indices: number[] = [];
    for (let i = 0; i < n; i++) {
      if (mask & (1 << i)) {
        indices.push(i);
      }
    }
    const k = indices.length;
    const sign: 1 | -1 = k % 2 === 1 ? 1 : -1;

    // Intersection of all sets at the selected indices.
    // `indices` is non-empty (mask starts at 1), so the slice is safe.
    const [first, ...rest] = indices.map((idx) => sets[idx] ?? []);
    let inter: string[] = first ?? [];
    for (const s of rest) {
      inter = intersect(inter, s);
    }
    const size = inter.length;

    terms.push({ sets: indices, sign, size });
    total += sign * size;
  }

  return { total, terms };
}
